import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded'
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded'
import LoginRoundedIcon from '@mui/icons-material/LoginRounded'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import isStrongPassword from 'validator/lib/isStrongPassword'
import API from '../../services/API'
import Loading from '../loading/loadingPage'
import SplitPage from '../../components/SplitPage/SplitPage'

function useQuery() {
  const { search } = useLocation()
  return useMemo(() => new URLSearchParams(search), [search])
}

const ForgotPasswordPage = ({ withToken, isLoggedIn, firstPassword }) => {
  const [loading, setLoading] = useState(true)
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [mismatch, setMismatch] = useState(false)
  const [weakPassword, setWeakPassword] = useState(false)
  const [token, setToken] = useState('')
  const [invalidToken, setInvalidToken] = useState(false)
  const [loginDisabled, setLoginDisabled] = useState(false)
  const [passwordAlreadySet, setPasswordAlreadySet] = useState(false)
  const [passwordUpdated, setPasswordUpdated] = useState(false)
  const [noMatchingUser, setNoMatchingUser] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const query = useQuery()
  const navigate = useNavigate()

  useEffect(() => {
    const queryToken = query.get('token')
    setToken(queryToken)

    if (isLoggedIn || ((withToken || firstPassword) && !queryToken)) {
      navigate('/')
      return
    }

    if (withToken) {
      API.post(`/resetpassword-checktoken?token=${queryToken}`)
        .then((res) => {
          if (res.data === 'invalidToken') {
            setInvalidToken(true)
          } else if (res.data === 'hasLoggedIn') {
            setPasswordAlreadySet(true)
          } else if (res.data === 'loginDisabled') {
            setLoginDisabled(true)
          }
        })
        .catch(() => setInvalidToken(true))
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [withToken, firstPassword, isLoggedIn, navigate, query])

  const handleSubmitForgot = (e) => {
    e.preventDefault()

    setIsSubmitting(true)
    setNoMatchingUser(false)
    setLoginDisabled(false)
    setSubmitError(false)

    API.post('/auth/forgot-password', { email })
      .then((res) => {
        if (res.status === 200) {
          setSent(true)
          return
        }

        if (res.status === 404 && res.data?.error === 'No such record found') {
          setNoMatchingUser(true)
          return
        }

        if (res.status === 403 && res.data?.errorCode === 'loginDisabled') {
          setLoginDisabled(true)
          return
        }

        setSubmitError(true)
      })
      .catch(() => setSubmitError(true))
      .finally(() => setIsSubmitting(false))
  }

  const handleSubmitReset = (e) => {
    e.preventDefault()

    setMismatch(false)
    setWeakPassword(false)
    setSubmitError(false)

    if (password !== confirmPassword) {
      setMismatch(true)
      return
    }

    if (
      !isStrongPassword(password, {
        minLength: 8,
        minUppercase: 1,
        minNumbers: 1,
        minSymbols: 1,
      })
    ) {
      setWeakPassword(true)
      return
    }

    setIsSubmitting(true)

    API.post(`/resetpassword?token=${token}`, { password })
      .then((res) => {
        if (res.status === 200) {
          setSent(true)
          setPasswordUpdated(true)
          return
        }

        if (['invalidToken', 'tokenExpired'].includes(res.data?.errorCode)) {
          setInvalidToken(true)
          return
        }

        if (res.data?.errorCode === 'loginDisabled') {
          setLoginDisabled(true)
          return
        }

        setSubmitError(true)
      })
      .catch(() => setInvalidToken(true))
      .finally(() => setIsSubmitting(false))
  }

  const loginDisabledAlert = (
    <Alert severity="error">
      <AlertTitle>Login disabled</AlertTitle>
      Login has been disabled for this account, so the password cannot be
      reset. Please contact your organisation's administrator.
    </Alert>
  )

  const pageTitle = withToken ? 'Reset your password' : 'Forgot password?'
  const pageSubtitle = withToken
    ? 'Create a new secure password for your AmbuNet account.'
    : 'Enter your email address and we’ll send you a password reset link.'

  return (
    <SplitPage>
      <Box
        sx={{
          width: '100%',
          height: '100%',
        }}
      >
        <Box
          sx={{
            p: 3,
            background:
              'linear-gradient(135deg, #242F40 0%, #2c3e50 65%, #239AA4 150%)',
            color: 'white',
          }}
        >
          <Stack spacing={1}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'rgba(255,255,255,0.14)',
                border: '1px solid rgba(255,255,255,0.22)',
              }}
            >
              {withToken ? (
                <LockResetRoundedIcon />
              ) : (
                <MarkEmailReadRoundedIcon />
              )}
            </Box>

            <Typography variant="h5" fontWeight={900} color="white">
              {pageTitle}
            </Typography>

            <Typography variant="body2" sx={{ opacity: 0.84 }} color="white">
              {pageSubtitle}
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ p: 3 }}>
          {loading ? (
            <Loading />
          ) : invalidToken ? (
            <Stack spacing={2}>
              <Alert severity="error">
                <AlertTitle>Link expired</AlertTitle>
                Sorry, this password reset link has expired or is no longer
                valid.
              </Alert>

              <Button
                variant="contained"
                startIcon={<HomeRoundedIcon />}
                onClick={() => navigate('/')}
                sx={{ borderRadius: 999, textTransform: 'none' }}
              >
                Return home
              </Button>
            </Stack>
          ) : passwordAlreadySet ? (
            <Stack spacing={2}>
              <Alert severity="info">
                <AlertTitle>Password already set</AlertTitle>A password has
                already been set for this account.
              </Alert>

              <Button
                variant="contained"
                startIcon={<LoginRoundedIcon />}
                onClick={() => navigate('/')}
                sx={{ borderRadius: 999, textTransform: 'none' }}
              >
                Go to login
              </Button>
            </Stack>
          ) : withToken && loginDisabled ? (
            <Stack spacing={2}>
              {loginDisabledAlert}

              <Button
                variant="contained"
                startIcon={<HomeRoundedIcon />}
                onClick={() => navigate('/')}
                sx={{ borderRadius: 999, textTransform: 'none' }}
              >
                Return home
              </Button>
            </Stack>
          ) : (
            <Box
              component="form"
              noValidate
              onSubmit={withToken ? handleSubmitReset : handleSubmitForgot}
            >
              <Stack spacing={2}>
                {withToken ? (
                  <>
                    <TextField
                      required
                      type="password"
                      inputProps={{ autoComplete: 'new-password' }}
                      fullWidth
                      autoFocus
                      label="New password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isSubmitting || sent}
                    />

                    <TextField
                      required
                      type="password"
                      fullWidth
                      inputProps={{ autoComplete: 'new-password' }}
                      label="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isSubmitting || sent}
                    />
                  </>
                ) : (
                  <TextField
                    required
                    fullWidth
                    id="email"
                    label="Email address"
                    name="email"
                    type="email"
                    autoFocus
                    onChange={(e) => setEmail(e.target.value)}
                    value={email}
                    disabled={isSubmitting || sent}
                  />
                )}

                {sent && !withToken && (
                  <Alert severity="success">
                    <AlertTitle>Password reset email sent</AlertTitle>
                    Please check your inbox for the reset link.
                  </Alert>
                )}

                {sent && withToken && passwordUpdated && (
                  <Alert severity="success">
                    <AlertTitle>Password reset successfully</AlertTitle>
                    You can now sign in with your new password.
                  </Alert>
                )}

                {noMatchingUser && (
                  <Alert severity="error">No matching user was found.</Alert>
                )}

                {loginDisabled && loginDisabledAlert}

                {mismatch && (
                  <Alert severity="error">Passwords do not match.</Alert>
                )}

                {weakPassword && (
                  <Alert severity="warning">
                    <AlertTitle>Password too weak</AlertTitle>
                    Passwords need at least 8 characters, 1 uppercase letter, 1
                    number and 1 symbol.
                  </Alert>
                )}

                {submitError && (
                  <Alert severity="error">
                    Something went wrong. Please try again.
                  </Alert>
                )}

                {sent && withToken && passwordUpdated ? (
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<LoginRoundedIcon />}
                    onClick={() => navigate('/')}
                    sx={{
                      borderRadius: 999,
                      py: 1.2,
                      textTransform: 'none',
                      fontWeight: 800,
                    }}
                  >
                    Go to login
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    startIcon={
                      withToken ? (
                        <LockResetRoundedIcon />
                      ) : (
                        <MarkEmailReadRoundedIcon />
                      )
                    }
                    disabled={
                      isSubmitting ||
                      sent ||
                      (withToken && !password) ||
                      (!withToken && !email)
                    }
                    sx={{
                      borderRadius: 999,
                      py: 1.2,
                      textTransform: 'none',
                      fontWeight: 800,
                    }}
                  >
                    {isSubmitting
                      ? 'Please wait...'
                      : withToken
                        ? 'Reset password'
                        : 'Send reset link'}
                  </Button>
                )}

                <Divider />

                <Button
                  variant="outlined"
                  onClick={() => navigate('/')}
                  sx={{ borderRadius: 999, textTransform: 'none' }}
                >
                  Back to login
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </SplitPage>
  )
}

export default ForgotPasswordPage
